// Route `api` cho `finVoucher` (K-DT): `/v1/finance/opscost` (TblOpsDN, SQL 018, chặn quyền tiền) → phiếu đề nghị chi phí.
// DB không có NCC / phần có hoá đơn / cờ rủi ro: để rỗng / 0 / false, không bịa. Tiền đổi sang triệu đồng như board.
import type { ApiRoute } from '@hana/data';
import type { CostLine, DocKind, SupplierDoc, DocOwner, Nature, PayState, Payer, SupplierPayment, Voucher, VoucherStatus } from './types';

type Row = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number.parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
const tr = (v: unknown) => Math.round((num(v) / 1e6) * 100) / 100;

/** Chuỗi DNStatus: Uploaded → Confirmed → Approved → RQ Payment → Payment Done; nhánh Need Revise. */
export function voucherStatus(dn: unknown): VoucherStatus {
  const s = str(dn).toLowerCase();
  if (s.startsWith('payment done')) return 'paid';
  if (s.startsWith('rq payment')) return 'rq';
  if (s.startsWith('approved')) return 'gd';
  if (s.startsWith('confirmed')) return 'kt';
  if (s.startsWith('need revise') || s.startsWith('revise')) return 'back';
  return 'c1';
}

const day = (v: unknown): string => str(v).slice(0, 10);

export function voucherFromApi(r: Row): Voucher {
  const no = str(r.OpsDoc);
  const total = tr(r.TotalAmount);
  const maker = str(r.OpsID);
  const payer: Payer = /^ODN/.test(no) && maker !== '' ? 'EMP' : 'BNK';
  return {
    no, date: day(r.OpsDate), maker, partner: '', job: str(r.JobID), total,
    withInvoice: 0, noInvoice: 0, onBehalf: 0,
    status: voucherStatus(r.DNStatus), autoApproved: false, partnerRisk: false,
    group: str(r.ServiceCode), duplicate: false, urgent: false, slow: false, payer,
  };
}

const items = (json: unknown): Row[] => {
  const list = (json as { items?: unknown } | null)?.items;
  return Array.isArray(list) ? (list as Row[]) : [];
};

export const voucherApiRoute: ApiRoute<Voucher> = {
  path: '/api/v1/finance/opscost',
  query: (q) => {
    const limit = q.limit && q.limit > 0 ? q.limit : 50;
    return { limit, page: Math.floor((q.offset ?? 0) / limit) + 1 };
  },
  parse: (json) => ({ items: items(json).filter((r) => str(r.OpsDoc)).map(voucherFromApi), hasMore: (json as { hasMore?: boolean } | null)?.hasMore === true }),
};

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** `2026-10-22T…` → `22/Oct/2026` (dạng board dùng). */
export function dayLabel(v: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(v));
  return m ? `${m[3]}/${MON[Number(m[2]) - 1] ?? m[2]}/${m[1]}` : '';
}

/** TblSupplierPayment: Approved → ok, PMStatus Done → paid, còn lại chờ duyệt. */
export function payState(approved: unknown, pm: unknown): PayState {
  if (str(pm).toLowerCase() === 'done') return 'paid';
  return approved === true ? 'ok' : 'wait';
}

/** DB không có người duyệt / chứng từ gộp / cờ rủi ro phụ: để rỗng, không bịa. Không cột tài khoản ngân hàng (chỉ nhãn TK chi). */
export function paymentFromApi(r: Row): SupplierPayment {
  return {
    ref: str(r.REF), partner: str(r.ShortName) || str(r.NAMENCC), date: dayLabel(r.DATEPAYNCC), cls: str(r.ClassifyColumn), account: str(r.Account),
    maker: str(r.USER), approver: '', state: payState(r.Approved, r.PMStatus), docs: [], amount: tr(r.AMOUNT), content: str(r.NOTE), extraRisk: [],
  };
}

export const paymentApiRoute: ApiRoute<SupplierPayment> = {
  path: '/api/v1/finance/supplierpay',
  query: voucherApiRoute.query,
  parse: (json) => ({ items: items(json).filter((r) => str(r.REF)).map(paymentFromApi), hasMore: (json as { hasMore?: boolean } | null)?.hasMore === true }),
};

/** OpsCostData.TypeInvoiceCost → chứng từ + chủ chứng từ: HĐ khách → INV/customer · HĐ công ty → INV/hana · Không HĐ → NDC/none. */
export function costDoc(t: unknown): { doc: DocKind; docOwner: DocOwner } {
  const s = str(t).toLowerCase();
  if (s.includes('khách')) return { doc: 'INV', docOwner: 'customer' };
  if (s.includes('công ty')) return { doc: 'INV', docOwner: 'hana' };
  return { doc: 'NDC', docOwner: 'none' };
}

/** DB chưa có mã bản chất (thiết kế mới): suy mặc định từ chủ chứng từ — HĐ khách → OBH, HĐ HANA → BIL, không chứng từ → ABS (source `default`, KTT/OPS sửa sau). */
export function costNature(owner: DocOwner): Nature {
  return owner === 'customer' ? 'OBH' : owner === 'hana' ? 'BIL' : 'ABS';
}

/** OpsCostData: DB không có tên phí (SV) → nhãn = mã phí; mã trống → `OTH`. */
export function costLineFromApi(r: Row): CostLine {
  const { doc, docOwner } = costDoc(r.TypeInvoiceCost);
  const voucher = str(r.OpsDoc);
  const code = str(r.Code) || 'OTH';
  return {
    id: String(r.Id ?? ''), voucher, job: str(r.JobID), code, label: code, amount: tr(r.TotalAmount),
    nature: costNature(docOwner), payer: /^ODN/.test(voucher) ? 'EMP' : 'BNK', doc, docOwner, source: 'default',
  };
}

export const costLineApiRoute: ApiRoute<CostLine> = {
  path: '/api/v1/finance/costing',
  query: voucherApiRoute.query,
  parse: (json) => ({ items: items(json).filter((r) => r.Id != null).map(costLineFromApi), hasMore: (json as { hasMore?: boolean } | null)?.hasMore === true }),
};

/** TblSupplierDBN (chứng từ đòi tiền của NCC): DB không có hạn thanh toán → `later` (không bịa quá hạn); số tiền = TotalAmountVND đổi triệu. */
export function supplierDocFromApi(r: Row): SupplierDoc {
  return {
    id: str(r.IDDoc) || str(r.DocCode), partner: str(r.Supplier), job: str(r.JobID), mbl: str(r.MBL), code: str(r.ServiceCode) || 'OTH',
    type: str(r.TypeDoc), status: str(r.DNStatus), due: 'later', days: 99, amount: tr(r.TotalAmountVND),
  };
}

export const supplierDocApiRoute: ApiRoute<SupplierDoc> = {
  path: '/api/v1/finance/settle',
  query: voucherApiRoute.query,
  parse: (json) => ({ items: items(json).filter((r) => str(r.IDDoc) || str(r.DocCode)).map(supplierDocFromApi), hasMore: (json as { hasMore?: boolean } | null)?.hasMore === true }),
};
